import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn, useListenForVirtualKeyboard } from '../../utils/index';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({
  children,
  container,
  ...props
}: DialogPrimitive.DialogPortalProps): JSX.Element => {
  const ref = React.useRef<HTMLDivElement>(null);
  const virtualKeyboardIsOpen = useListenForVirtualKeyboard(ref);
  return (
    <DialogPrimitive.Portal
      container={container || document.getElementById('react-portal')}
      {...props}
    >
      <div
        ref={ref}
        className={cn(
          'tw-fixed tw-inset-0 tw-z-[39] tw-flex tw-justify-center tw-p-10 sm:tw-items-center',
          {
            'sm:tw-items-start': virtualKeyboardIsOpen,
          },
        )}
      >
        {children}
      </div>
    </DialogPrimitive.Portal>
  );
};
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'tw-animate-fade-in tw-fixed tw-inset-0 tw-left-0 tw-top-0 tw-z-[39] tw-h-full tw-w-full tw-bg-black/50 tw-backdrop-blur-sm tw-transition-opacity',
      className,
    )}
    {...props}
    ref={ref}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    container?: HTMLElement;
  }
>(({ className, children, container, ...props }, ref) => (
  <DialogPortal container={container}>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'tw-animate-fade-in tw-fixed tw-z-[39] tw-flex tw-w-full tw-scale-100 tw-flex-col tw-gap-4 tw-overflow-hidden tw-rounded-[30px] tw-bg-white tw-opacity-100 tw-slide-in-from-bottom-10 sm:tw-max-w-lg sm:tw-zoom-in-90 sm:tw-slide-in-from-bottom-0',
        className,
      )}
      {...props}
    >
      {children}
      {props.showCloseButton && (
        <DialogPrimitive.Close className="tw-absolute tw-right-4 tw-top-4 tw-rounded-sm tw-opacity-70 tw-transition-opacity hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-400 focus:tw-ring-offset-2 disabled:tw-pointer-events-none data-[state=open]:tw-bg-slate-100">
          <span className="tw-sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn('tw-flex tw-flex-col tw-space-y-2 tw-text-center sm:tw-text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn(
      'tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'tw-text-lg tw-font-semibold tw-text-slate-900',
      'dark:tw-text-slate-50',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('tw-text-sm tw-text-slate-500', 'dark:tw-text-slate-400', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
